import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import UploadIcon from '@mui/icons-material/Upload';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import pptPoliciesService from '../../services/pptPoliciesService';
import { API_BASE_URL } from '../../config';
import {
  IPptPolicyAdminResponse,
  IPptPolicyChecklistItem,
  IPptPolicyContent,
  IPptPolicyDownload,
  IPptPolicyExample,
  IPptPolicyFaqItem,
  IPptPolicyImage,
  IPptPolicySection,
} from '../../types/pptPolicies';

const COLORS = {
  olympia: '#1B3967',
  olympiaLight: '#2a5087',
  sandLight: '#f8f5ef',
  sandDark: '#D4CFC3',
  success: '#2e7d32',
  error: '#c62828',
  cardBorder: 'rgba(0,0,0,0.06)',
  doBg: 'rgba(46,125,50,0.06)',
  doBorder: 'rgba(46,125,50,0.22)',
  dontBg: 'rgba(198,40,40,0.05)',
  dontBorder: 'rgba(198,40,40,0.18)',
};

const createEmptyChecklistItem = (displayOrder: number): IPptPolicyChecklistItem => ({
  text: '',
  displayOrder,
});

const createEmptyImage = (displayOrder: number): IPptPolicyImage => ({
  url: '',
  alt: '',
  caption: '',
  displayOrder,
});

const createEmptySection = (displayOrder: number): IPptPolicySection => ({
  title: '',
  anchor: '',
  introText: '',
  content: '',
  displayOrder,
  images: [],
});

const createEmptyExample = (displayOrder: number): IPptPolicyExample => ({
  type: 'do',
  title: '',
  description: '',
  imageUrl: '',
  displayOrder,
});

const createEmptyFaqItem = (displayOrder: number): IPptPolicyFaqItem => ({
  question: '',
  answer: '',
  displayOrder,
});

const createEmptyDownload = (displayOrder: number): IPptPolicyDownload => ({
  title: '',
  description: '',
  category: '',
  usageType: '',
  versionLabel: '',
  fileType: '',
  fileName: '',
  fileUrl: '',
  thumbnailUrl: '',
  previewUrl: '',
  isTemplate: true,
  isExample: false,
  visibleInPlugin: true,
  displayOrder,
});

const createEmptyDraft = (): IPptPolicyContent => ({
  title: 'PPT Policys',
  intro: '',
  ownerName: '',
  ownerTeam: '',
  tocEnabled: true,
  quickChecklist: [],
  sections: [],
  examples: [],
  faqItems: [],
  downloads: [],
});

const resolveAssetUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE_URL}${url}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Inte publicerad';
  try {
    return new Date(value).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const titleToAnchor = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9åäö]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '') || 'sektion';

const HelperText = ({ text }: { text: string }) => (
  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.25 }}>
    {text}
  </Typography>
);

const SectionHeader = ({
  title,
  subtitle,
  count,
  countLabel,
  onAdd,
  addLabel,
}: {
  title: string;
  subtitle?: string;
  count: number;
  countLabel: string;
  onAdd: () => void;
  addLabel: string;
}) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
    <Box>
      <Typography variant="h6" sx={{ color: COLORS.olympia, fontWeight: 700, fontSize: '1.1rem' }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
        <Chip label={`${count} ${countLabel}`} size="small" sx={{ fontWeight: 600, height: 24, fontSize: '0.75rem' }} />
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Box>
    <Button
      startIcon={<AddIcon />}
      onClick={onAdd}
      size="small"
      variant="outlined"
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 2,
        borderColor: COLORS.olympia,
        color: COLORS.olympia,
        '&:hover': { borderColor: COLORS.olympiaLight, bgcolor: 'rgba(27,57,103,0.04)' },
      }}
    >
      {addLabel}
    </Button>
  </Stack>
);

interface PptPoliciesManagementPageProps {
  canPublish?: boolean;
}

const PptPoliciesManagementPage: React.FC<PptPoliciesManagementPageProps> = ({ canPublish = false }) => {
  const [draft, setDraft] = useState<IPptPolicyContent>(createEmptyDraft());
  const [publishedSnapshot, setPublishedSnapshot] = useState<IPptPolicyContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [meta, setMeta] = useState<Pick<IPptPolicyAdminResponse, 'lastPublishedAt' | 'updatedAt'>>({
    lastPublishedAt: null,
    updatedAt: '',
  });

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await pptPoliciesService.fetchAdminPage();
      setDraft(response.draft || createEmptyDraft());
      setPublishedSnapshot(response.published || null);
      setMeta({
        lastPublishedAt: response.lastPublishedAt,
        updatedAt: response.updatedAt,
      });
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte hämta PPT Policys-admin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const counts = useMemo(
    () => ({
      checklist: draft.quickChecklist.length,
      sections: draft.sections.length,
      examples: draft.examples.length,
      faq: draft.faqItems.length,
      downloads: draft.downloads.length,
    }),
    [draft]
  );

  const updateRootField = <K extends keyof IPptPolicyContent>(field: K, value: IPptPolicyContent[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateChecklistItem = (index: number, patch: Partial<IPptPolicyChecklistItem>) => {
    setDraft((prev) => ({
      ...prev,
      quickChecklist: prev.quickChecklist.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const updateSection = (index: number, patch: Partial<IPptPolicySection>) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, ...patch };
        if (patch.title && !patch.anchor) {
          updated.anchor = titleToAnchor(patch.title);
        }
        return updated;
      }),
    }));
  };

  const updateSectionImage = (sectionIndex: number, imageIndex: number, patch: Partial<IPptPolicyImage>) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section, si) => {
        if (si !== sectionIndex) return section;
        return {
          ...section,
          images: section.images.map((image, ii) => (ii === imageIndex ? { ...image, ...patch } : image)),
        };
      }),
    }));
  };

  const updateExample = (index: number, patch: Partial<IPptPolicyExample>) => {
    setDraft((prev) => ({
      ...prev,
      examples: prev.examples.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const updateFaqItem = (index: number, patch: Partial<IPptPolicyFaqItem>) => {
    setDraft((prev) => ({
      ...prev,
      faqItems: prev.faqItems.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const updateDownload = (index: number, patch: Partial<IPptPolicyDownload>) => {
    setDraft((prev) => ({
      ...prev,
      downloads: prev.downloads.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const removeChecklistItem = (index: number) => {
    setDraft((prev) => ({ ...prev, quickChecklist: prev.quickChecklist.filter((_, i) => i !== index) }));
  };
  const removeSection = (index: number) => {
    setDraft((prev) => ({ ...prev, sections: prev.sections.filter((_, i) => i !== index) }));
  };
  const removeSectionImage = (sectionIndex: number, imageIndex: number) => {
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((s, si) =>
        si === sectionIndex ? { ...s, images: s.images.filter((_, ii) => ii !== imageIndex) } : s
      ),
    }));
  };
  const removeExample = (index: number) => {
    setDraft((prev) => ({ ...prev, examples: prev.examples.filter((_, i) => i !== index) }));
  };
  const removeFaqItem = (index: number) => {
    setDraft((prev) => ({ ...prev, faqItems: prev.faqItems.filter((_, i) => i !== index) }));
  };
  const removeDownload = (index: number) => {
    setDraft((prev) => ({ ...prev, downloads: prev.downloads.filter((_, i) => i !== index) }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSuccessMessage(null);
    try {
      const response = await pptPoliciesService.updateDraft(draft);
      setDraft(response.draft || createEmptyDraft());
      setPublishedSnapshot(response.published || null);
      setMeta({ lastPublishedAt: response.lastPublishedAt, updatedAt: response.updatedAt });
      setError(null);
      setSuccessMessage('Utkast sparat.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte spara utkastet.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setConfirmPublishOpen(false);
    setPublishing(true);
    setSuccessMessage(null);
    try {
      await pptPoliciesService.updateDraft(draft);
      await pptPoliciesService.publishDraft();
      await loadPage();
      setError(null);
      setSuccessMessage('PPT Policys publicerad!');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte publicera PPT Policys.');
    } finally {
      setPublishing(false);
    }
  };

  const handleSectionImageUpload = async (sectionIndex: number, file: File) => {
    setUploadingLabel(`Laddar upp bild till sektion ${sectionIndex + 1}...`);
    try {
      const result = await pptPoliciesService.uploadImage(file);
      setDraft((prev) => ({
        ...prev,
        sections: prev.sections.map((section, si) =>
          si === sectionIndex
            ? {
                ...section,
                images: [
                  ...section.images,
                  {
                    ...createEmptyImage(section.images.length ? Math.max(...section.images.map((img) => img.displayOrder ?? 0)) + 1 : 0),
                    url: result.url,
                    alt: file.name,
                  },
                ],
              }
            : section
        ),
      }));
      setSuccessMessage('Bild uppladdad.');
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp bilden.');
    } finally {
      setUploadingLabel(null);
    }
  };

  const handleExampleImageUpload = async (exampleIndex: number, file: File) => {
    setUploadingLabel(`Laddar upp exempelbild ${exampleIndex + 1}...`);
    try {
      const result = await pptPoliciesService.uploadImage(file);
      updateExample(exampleIndex, { imageUrl: result.url });
      setSuccessMessage('Exempelbild uppladdad.');
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp bilden.');
    } finally {
      setUploadingLabel(null);
    }
  };

  const handleDownloadFileUpload = async (downloadIndex: number, file: File) => {
    setUploadingLabel(`Laddar upp fil ${downloadIndex + 1}...`);
    try {
      const result = await pptPoliciesService.uploadFile(file);
      updateDownload(downloadIndex, {
        fileUrl: result.url,
        fileName: result.fileName,
        fileType: result.fileType,
      });
      setSuccessMessage('Fil uppladdad.');
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Kunde inte ladda upp filen.');
    } finally {
      setUploadingLabel(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: COLORS.olympia }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Laddar administration...</Typography>
      </Box>
    );
  }

  const paperSx = { p: { xs: 2.5, md: 3.5 }, borderRadius: 3, border: `1px solid ${COLORS.cardBorder}` };

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      {/* Publish confirmation dialog */}
      <Dialog open={confirmPublishOpen} onClose={() => setConfirmPublishOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Publicera PPT Policys?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Utkastet sparas och ersätter den publicerade versionen. Alla användare med behörighet kommer se den nya versionen direkt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublishOpen(false)}>Avbryt</Button>
          <Button
            onClick={handlePublish}
            variant="contained"
            sx={{ bgcolor: COLORS.olympia, '&:hover': { bgcolor: COLORS.olympiaLight } }}
          >
            Publicera
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${COLORS.olympia} 0%, #0d2847 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: -100, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }} />
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '2rem', fontWeight: 800, mb: 0.5, letterSpacing: '-0.02em' }}>PPT Policys</Typography>
            <Typography sx={{ opacity: 0.85, maxWidth: 760, lineHeight: 1.7 }}>
              Administrera policytext, checklistor, dos &amp; don&apos;ts, FAQ och PowerPoint-mallar.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
              <Chip size="small" label={`Sparad: ${formatDate(meta.updatedAt)}`} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white' }} />
              <Chip size="small" label={`Publicerad: ${formatDate(meta.lastPublishedAt)}`} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white' }} />
            </Stack>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: COLORS.olympia }} /> : <SaveIcon />}
              onClick={handleSaveDraft}
              disabled={saving || publishing}
              sx={{
                bgcolor: 'white',
                color: COLORS.olympia,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2.5,
                px: 3,
                '&:hover': { bgcolor: '#eef3f8' },
              }}
            >
              {saving ? 'Sparar...' : 'Spara utkast'}
            </Button>
            {canPublish && (
              <Button
                variant="outlined"
                startIcon={publishing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PublishIcon />}
                onClick={() => setConfirmPublishOpen(true)}
                disabled={saving || publishing}
                sx={{
                  borderColor: 'rgba(255,255,255,0.4)',
                  color: 'white',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2.5,
                  px: 3,
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                {publishing ? 'Publicerar...' : 'Publicera'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {uploadingLabel && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2.5, borderRadius: 2 }}>
          {uploadingLabel}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Basic info */}
        <Paper elevation={0} sx={paperSx}>
          <Typography variant="h6" sx={{ color: COLORS.olympia, fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}>
            Grundinformation
          </Typography>
          <HelperText text="Dessa fält visas i hero-sektionen på den publika sidan." />
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            <TextField
              label="Sidtitel"
              value={draft.title}
              onChange={(e) => updateRootField('title', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Introduktion"
              value={draft.intro}
              onChange={(e) => updateRootField('intro', e.target.value)}
              fullWidth
              multiline
              minRows={3}
              size="small"
              helperText="Visas under rubriken i hero-sektionen. Max 6000 tecken."
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Ansvarig person"
                value={draft.ownerName}
                onChange={(e) => updateRootField('ownerName', e.target.value)}
                fullWidth
                size="small"
                helperText="T.ex. namn och roll"
              />
              <TextField
                label="Ansvarigt team"
                value={draft.ownerTeam}
                onChange={(e) => updateRootField('ownerTeam', e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.tocEnabled}
                  onChange={(e) => updateRootField('tocEnabled', e.target.checked)}
                />
              }
              label="Visa innehållsförteckning i läsvyn"
            />
          </Stack>
        </Paper>

        {/* Checklist */}
        <Paper elevation={0} sx={paperSx}>
          <SectionHeader
            title="Snabbguide"
            subtitle="Visas som en checklista högst upp"
            count={counts.checklist}
            countLabel={counts.checklist === 1 ? 'punkt' : 'punkter'}
            onAdd={() =>
              updateRootField('quickChecklist', [
                ...draft.quickChecklist,
                createEmptyChecklistItem(
                  draft.quickChecklist.length ? Math.max(...draft.quickChecklist.map((i) => i.displayOrder ?? 0)) + 1 : 0
                ),
              ])
            }
            addLabel="Ny punkt"
          />
          <Stack spacing={1.5}>
            {draft.quickChecklist.map((item, index) => (
              <Stack
                key={item._id || `check-${index}`}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.cardBorder}`,
                  bgcolor: 'white',
                  '&:hover': { borderColor: COLORS.sandDark },
                }}
              >
                <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                <TextField
                  placeholder={`Checklistpunkt ${index + 1}`}
                  value={item.text}
                  onChange={(e) => updateChecklistItem(index, { text: e.target.value })}
                  fullWidth
                  size="small"
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                />
                <TextField
                  type="number"
                  value={item.displayOrder}
                  onChange={(e) => updateChecklistItem(index, { displayOrder: Number(e.target.value) })}
                  sx={{ width: 70 }}
                  size="small"
                  inputProps={{ style: { textAlign: 'center' } }}
                />
                <Tooltip title="Ta bort">
                  <IconButton size="small" color="error" onClick={() => removeChecklistItem(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
            {draft.quickChecklist.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography variant="body2">Inga checklistpunkter. Klicka &quot;Ny punkt&quot; ovan.</Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Sections */}
        <Paper elevation={0} sx={paperSx}>
          <SectionHeader
            title="Policysektioner"
            subtitle="Var och en visas som en rubrik med innehåll"
            count={counts.sections}
            countLabel={counts.sections === 1 ? 'sektion' : 'sektioner'}
            onAdd={() =>
              updateRootField('sections', [
                ...draft.sections,
                createEmptySection(draft.sections.length ? Math.max(...draft.sections.map((i) => i.displayOrder ?? 0)) + 1 : 0),
              ])
            }
            addLabel="Ny sektion"
          />
          <Stack spacing={2}>
            {draft.sections.map((section, si) => (
              <Accordion
                key={section._id || `section-${si}`}
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: COLORS.olympia,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.78rem',
                        flexShrink: 0,
                      }}
                    >
                      {si + 1}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {section.title || `Ny sektion ${si + 1}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        #{section.anchor || 'auto'}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 3 }}>
                  <Stack spacing={2.5}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        label="Rubrik"
                        value={section.title}
                        onChange={(e) => updateSection(si, { title: e.target.value })}
                        fullWidth
                        size="small"
                        helperText="Anchor genereras automatiskt från rubriken"
                      />
                      <TextField
                        label="Anchor"
                        value={section.anchor}
                        onChange={(e) => updateSection(si, { anchor: e.target.value })}
                        fullWidth
                        size="small"
                        helperText="Används för scroll-länkar"
                      />
                      <TextField
                        label="Ordning"
                        type="number"
                        value={section.displayOrder}
                        onChange={(e) => updateSection(si, { displayOrder: Number(e.target.value) })}
                        sx={{ width: { xs: '100%', md: 120 } }}
                        size="small"
                      />
                    </Stack>
                    <TextField
                      label="Ingress / introtext"
                      value={section.introText}
                      onChange={(e) => updateSection(si, { introText: e.target.value })}
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                    />
                    <TextField
                      label="Brödtext"
                      value={section.content}
                      onChange={(e) => updateSection(si, { content: e.target.value })}
                      fullWidth
                      multiline
                      minRows={4}
                      size="small"
                    />

                    {/* Section images */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, backgroundColor: COLORS.sandLight, border: `1px solid ${COLORS.sandDark}` }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Bilder i sektionen</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button component="label" size="small" variant="outlined" startIcon={<UploadIcon />} sx={{ textTransform: 'none', borderRadius: 2 }}>
                            Ladda upp
                            <input
                              hidden
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (file) await handleSectionImageUpload(si, file);
                                e.target.value = '';
                              }}
                            />
                          </Button>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              updateSection(si, {
                                images: [
                                  ...section.images,
                                  createEmptyImage(section.images.length ? Math.max(...section.images.map((img) => img.displayOrder ?? 0)) + 1 : 0),
                                ],
                              })
                            }
                            sx={{ textTransform: 'none' }}
                          >
                            Manuellt
                          </Button>
                        </Stack>
                      </Stack>

                      <Stack spacing={2}>
                        {section.images.map((image, ii) => (
                          <Paper key={image._id || `img-${ii}`} sx={{ p: 2, borderRadius: 2 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                              {image.url && (
                                <Box
                                  component="img"
                                  src={resolveAssetUrl(image.url)}
                                  alt={image.alt || 'Bild'}
                                  sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1.5, border: `1px solid ${COLORS.cardBorder}`, flexShrink: 0 }}
                                />
                              )}
                              <Stack spacing={1.5} sx={{ flex: 1, width: '100%' }}>
                                <TextField label="Bild-URL" value={image.url} onChange={(e) => updateSectionImage(si, ii, { url: e.target.value })} fullWidth size="small" />
                                <Stack direction="row" spacing={1.5}>
                                  <TextField label="Alt-text" value={image.alt} onChange={(e) => updateSectionImage(si, ii, { alt: e.target.value })} fullWidth size="small" />
                                  <TextField label="Bildtext" value={image.caption} onChange={(e) => updateSectionImage(si, ii, { caption: e.target.value })} fullWidth size="small" />
                                </Stack>
                              </Stack>
                              <Tooltip title="Ta bort bild">
                                <IconButton size="small" color="error" onClick={() => removeSectionImage(si, ii)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Paper>
                        ))}
                        {section.images.length === 0 && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 1 }}>
                            Inga bilder i denna sektion.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>

                    <Button
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => removeSection(si)}
                      size="small"
                      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      Ta bort sektion
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
            {draft.sections.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography variant="body2">Inga sektioner. Klicka &quot;Ny sektion&quot; ovan.</Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Dos and Don'ts */}
        <Paper elevation={0} sx={paperSx}>
          <SectionHeader
            title="Dos &amp; Don'ts"
            subtitle="Exempel visas grupperat efter typ"
            count={counts.examples}
            countLabel="exempel"
            onAdd={() =>
              updateRootField('examples', [
                ...draft.examples,
                createEmptyExample(draft.examples.length ? Math.max(...draft.examples.map((i) => i.displayOrder ?? 0)) + 1 : 0),
              ])
            }
            addLabel="Nytt exempel"
          />
          <Stack spacing={2}>
            {draft.examples.map((example, ei) => {
              const isDo = example.type === 'do';
              return (
                <Paper
                  key={example._id || `example-${ei}`}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: `1.5px solid ${isDo ? COLORS.doBorder : COLORS.dontBorder}`,
                    bgcolor: isDo ? COLORS.doBg : COLORS.dontBg,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                      <TextField
                        select
                        label="Typ"
                        value={example.type}
                        onChange={(e) => updateExample(ei, { type: e.target.value as 'do' | 'dont' })}
                        sx={{ width: { xs: '100%', md: 160 } }}
                        size="small"
                      >
                        <MenuItem value="do">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircleOutlineIcon sx={{ color: COLORS.success, fontSize: 18 }} />
                            <span>Do</span>
                          </Stack>
                        </MenuItem>
                        <MenuItem value="dont">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <HighlightOffIcon sx={{ color: COLORS.error, fontSize: 18 }} />
                            <span>Don&apos;t</span>
                          </Stack>
                        </MenuItem>
                      </TextField>
                      <TextField
                        label="Rubrik"
                        value={example.title}
                        onChange={(e) => updateExample(ei, { title: e.target.value })}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="Ordning"
                        type="number"
                        value={example.displayOrder}
                        onChange={(e) => updateExample(ei, { displayOrder: Number(e.target.value) })}
                        sx={{ width: { xs: '100%', md: 100 } }}
                        size="small"
                      />
                    </Stack>
                    <TextField
                      label="Beskrivning"
                      value={example.description}
                      onChange={(e) => updateExample(ei, { description: e.target.value })}
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                    />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                      <TextField
                        label="Bild-URL"
                        value={example.imageUrl}
                        onChange={(e) => updateExample(ei, { imageUrl: e.target.value })}
                        fullWidth
                        size="small"
                      />
                      <Button component="label" startIcon={<ImageIcon />} size="small" variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}>
                        Ladda upp
                        <input
                          hidden
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) await handleExampleImageUpload(ei, file);
                            e.target.value = '';
                          }}
                        />
                      </Button>
                      <Tooltip title="Ta bort exempel">
                        <IconButton color="error" size="small" onClick={() => removeExample(ei)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {example.imageUrl && (
                      <Box
                        component="img"
                        src={resolveAssetUrl(example.imageUrl)}
                        alt={example.title || `Exempel ${ei + 1}`}
                        sx={{ width: 200, maxWidth: '100%', borderRadius: 2, border: `1px solid ${COLORS.cardBorder}` }}
                      />
                    )}
                  </Stack>
                </Paper>
              );
            })}
            {draft.examples.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography variant="body2">Inga dos/don&apos;ts. Klicka &quot;Nytt exempel&quot; ovan.</Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* FAQ */}
        <Paper elevation={0} sx={paperSx}>
          <SectionHeader
            title="FAQ"
            subtitle="Visas som expanderbara frågor"
            count={counts.faq}
            countLabel={counts.faq === 1 ? 'fråga' : 'frågor'}
            onAdd={() =>
              updateRootField('faqItems', [
                ...draft.faqItems,
                createEmptyFaqItem(draft.faqItems.length ? Math.max(...draft.faqItems.map((i) => i.displayOrder ?? 0)) + 1 : 0),
              ])
            }
            addLabel="Ny fråga"
          />
          <Stack spacing={2}>
            {draft.faqItems.map((item, fi) => (
              <Paper
                key={item._id || `faq-${fi}`}
                elevation={0}
                sx={{ p: 2.5, borderRadius: 2.5, border: `1px solid ${COLORS.cardBorder}` }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <InfoOutlinedIcon sx={{ color: COLORS.olympia, mt: 0.5, flexShrink: 0 }} />
                    <TextField
                      label="Fråga"
                      value={item.question}
                      onChange={(e) => updateFaqItem(fi, { question: e.target.value })}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      type="number"
                      value={item.displayOrder}
                      onChange={(e) => updateFaqItem(fi, { displayOrder: Number(e.target.value) })}
                      sx={{ width: 80 }}
                      size="small"
                      inputProps={{ style: { textAlign: 'center' } }}
                    />
                    <Tooltip title="Ta bort fråga">
                      <IconButton size="small" color="error" onClick={() => removeFaqItem(fi)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField
                    label="Svar"
                    value={item.answer}
                    onChange={(e) => updateFaqItem(fi, { answer: e.target.value })}
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                  />
                </Stack>
              </Paper>
            ))}
            {draft.faqItems.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography variant="body2">Inga frågor. Klicka &quot;Ny fråga&quot; ovan.</Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Downloads */}
        <Paper elevation={0} sx={paperSx}>
          <SectionHeader
            title="Mallar &amp; filer"
            subtitle="Filer som kan laddas ner av användare"
            count={counts.downloads}
            countLabel={counts.downloads === 1 ? 'fil' : 'filer'}
            onAdd={() =>
              updateRootField('downloads', [
                ...draft.downloads,
                createEmptyDownload(draft.downloads.length ? Math.max(...draft.downloads.map((i) => i.displayOrder ?? 0)) + 1 : 0),
              ])
            }
            addLabel="Ny fil"
          />
          <Stack spacing={2}>
            {draft.downloads.map((download, di) => (
              <Accordion
                key={download._id || `dl-${di}`}
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <DescriptionIcon sx={{ color: COLORS.olympia }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>
                        {download.title || `Ny fil ${di + 1}`}
                      </Typography>
                      <Stack direction="row" spacing={0.75} sx={{ mt: 0.25 }}>
                        {download.fileType && <Chip label={download.fileType.toUpperCase()} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} />}
                        {download.isTemplate && <Chip label="Mall" size="small" sx={{ height: 20, fontSize: '0.68rem' }} />}
                        {download.isExample && <Chip label="Exempel" size="small" sx={{ height: 20, fontSize: '0.68rem' }} />}
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2.5, pb: 3 }}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField label="Titel" value={download.title} onChange={(e) => updateDownload(di, { title: e.target.value })} fullWidth size="small" />
                      <TextField label="Ordning" type="number" value={download.displayOrder} onChange={(e) => updateDownload(di, { displayOrder: Number(e.target.value) })} sx={{ width: { xs: '100%', md: 100 } }} size="small" />
                    </Stack>
                    <TextField label="Beskrivning" value={download.description} onChange={(e) => updateDownload(di, { description: e.target.value })} fullWidth multiline minRows={2} size="small" />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField label="Kategori" value={download.category} onChange={(e) => updateDownload(di, { category: e.target.value })} fullWidth size="small" helperText="T.ex. Intern, Extern, Försäljning" />
                      <TextField label="Användningsområde" value={download.usageType} onChange={(e) => updateDownload(di, { usageType: e.target.value })} fullWidth size="small" />
                      <TextField label="Version" value={download.versionLabel} onChange={(e) => updateDownload(di, { versionLabel: e.target.value })} fullWidth size="small" />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField label="Fil-URL" value={download.fileUrl} onChange={(e) => updateDownload(di, { fileUrl: e.target.value })} fullWidth size="small" />
                      <TextField label="Filnamn" value={download.fileName} onChange={(e) => updateDownload(di, { fileName: e.target.value })} fullWidth size="small" />
                      <TextField label="Filtyp" value={download.fileType} onChange={(e) => updateDownload(di, { fileType: e.target.value })} sx={{ width: { xs: '100%', md: 140 } }} size="small" helperText="pdf, pptx, potx" />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField label="Preview-URL" value={download.previewUrl} onChange={(e) => updateDownload(di, { previewUrl: e.target.value })} fullWidth size="small" />
                      <TextField label="Thumbnail-URL" value={download.thumbnailUrl} onChange={(e) => updateDownload(di, { thumbnailUrl: e.target.value })} fullWidth size="small" />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center">
                      <FormControlLabel
                        control={<Switch checked={download.isTemplate} onChange={(e) => updateDownload(di, { isTemplate: e.target.checked })} size="small" />}
                        label={<Typography variant="body2">Mall</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={download.isExample} onChange={(e) => updateDownload(di, { isExample: e.target.checked })} size="small" />}
                        label={<Typography variant="body2">Exempelfil</Typography>}
                      />
                      <FormControlLabel
                        control={<Switch checked={download.visibleInPlugin} onChange={(e) => updateDownload(di, { visibleInPlugin: e.target.checked })} size="small" />}
                        label={<Typography variant="body2">Visa i plugin</Typography>}
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                      <Button component="label" startIcon={<UploadIcon />} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Ladda upp fil
                        <input
                          hidden
                          type="file"
                          accept=".ppt,.pptx,.potx,.pdf"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) await handleDownloadFileUpload(di, file);
                            e.target.value = '';
                          }}
                        />
                      </Button>
                      {download.fileUrl && (
                        <Button
                          component="a"
                          href={resolveAssetUrl(download.fileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<DescriptionIcon />}
                          size="small"
                          sx={{ textTransform: 'none' }}
                        >
                          Öppna fil
                        </Button>
                      )}
                      <Button color="error" startIcon={<DeleteIcon />} onClick={() => removeDownload(di)} size="small" sx={{ textTransform: 'none' }}>
                        Ta bort
                      </Button>
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
            {draft.downloads.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <Typography variant="body2">Inga filer. Klicka &quot;Ny fil&quot; ovan.</Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Published snapshot */}
        <Paper elevation={0} sx={{ ...paperSx, bgcolor: COLORS.sandLight }}>
          <Typography variant="h6" sx={{ color: COLORS.olympia, fontWeight: 700, mb: 1.5, fontSize: '1.1rem' }}>
            Publicerad version
          </Typography>
          {publishedSnapshot ? (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Den publicerade versionen innehåller:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                <Chip size="small" label={`${publishedSnapshot.sections.length} sektioner`} variant="outlined" />
                <Chip size="small" label={`${publishedSnapshot.quickChecklist.length} checklistpunkter`} variant="outlined" />
                <Chip size="small" label={`${publishedSnapshot.examples.length} dos/don'ts`} variant="outlined" />
                <Chip size="small" label={`${publishedSnapshot.faqItems.length} FAQ`} variant="outlined" />
                <Chip size="small" label={`${publishedSnapshot.downloads.length} filer`} variant="outlined" />
              </Stack>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Ingen publicerad version finns. Klicka &quot;Publicera&quot; ovan för att göra utkastet tillgängligt för alla.
            </Typography>
          )}
        </Paper>

        {/* Sticky footer actions */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 3,
            position: 'sticky',
            bottom: 16,
            zIndex: 10,
            bgcolor: 'white',
            border: `1px solid ${COLORS.cardBorder}`,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center">
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
              onClick={handleSaveDraft}
              disabled={saving || publishing}
              sx={{
                bgcolor: COLORS.olympia,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2.5,
                px: 4,
                '&:hover': { bgcolor: COLORS.olympiaLight },
              }}
            >
              {saving ? 'Sparar...' : 'Spara utkast'}
            </Button>
            {canPublish && (
              <Button
                variant="outlined"
                startIcon={publishing ? <CircularProgress size={16} /> : <PublishIcon />}
                onClick={() => setConfirmPublishOpen(true)}
                disabled={saving || publishing}
                sx={{
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2.5,
                  px: 4,
                  borderColor: COLORS.olympia,
                  color: COLORS.olympia,
                }}
              >
                {publishing ? 'Publicerar...' : 'Publicera'}
              </Button>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default PptPoliciesManagementPage;
